import { Store } from 'lucide-react';

import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { useCheckPermission } from '@/hooks/useCheckPermission';

const PAID_ORDER_TYPES = new Set(['click-collect']);

function formatPrice(order) {
	const product = order.products?.[0];
	const total =
		order.products?.reduce((sum, p) => sum + (Number(p.price) || 0) * (Number(p.quantity) || 1), 0) ?? Number(product?.price) ?? 0;
	const currency = product?.currency || 'GBP';
	return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(total);
}

/**
 * Detail view for a single order. Mirrors the visual shape of
 * `fresh-dashboard/src/components/orders/OrderDetailModal.tsx`:
 *
 *   shop strip  →  Order #ref heading  →  "1 x Product"  →  SKU  →
 *   price  →  PAID ONLINE / PAY IN STORE  →  customer block  →  note
 *
 * Single-product rendering is intentional — multi-line orders aren't a real
 * thing on the Unify side yet (see ticket NEA-5719 §"Out of scope").
 *
 * Action buttons are gated on `org:orders:manage`. "Mark complete" is only
 * shown when the order is `confirmed`; it posts immediately via `onComplete`.
 * "Cancel order" is shown for `reserved` / `paid` / `confirmed`; clicking it
 * defers to `onCancel`, which the parent uses to open the email modal.
 */
export function OrderDetailModal({ order, shop, open, onOpenChange, onConfirm, onComplete, onCancel }) {
	const canManage = useCheckPermission('org:orders:manage');

	if (!order) return null;

	const product = order.products?.[0] || {};
	const orderRef = order.pickupReference || order.id;
	const paidOnline = PAID_ORDER_TYPES.has(order.orderType);
	const paymentLabel = paidOnline ? 'PAID ONLINE' : 'PAY IN STORE';
	const paymentClass = paidOnline ? 'text-success' : 'text-action';

	const canConfirm = order.status === 'reserved' || order.status === 'paid';
	const canMarkComplete = order.status === 'confirmed';
	const canCancel = order.status === 'reserved' || order.status === 'paid' || order.status === 'confirmed';

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="rounded-2xl sm:max-w-md">
				<div className="flex items-center gap-2 text-sm text-muted-foreground">
					<span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-muted">
						<Store className="h-3.5 w-3.5" />
					</span>
					{shop?.name}
				</div>

				<DialogTitle className="mt-1 text-2xl font-extrabold tracking-tight">Order {orderRef}</DialogTitle>

				<DialogDescription asChild>
					<div className="space-y-3 pt-1">
						<div className="text-lg font-semibold leading-snug text-foreground">
							{Number(product.quantity) || 1} x {product.name || 'Order'}
						</div>
						{product.barcode ? <div className="text-sm text-muted-foreground">{product.barcode}</div> : null}
						<div className="text-base font-semibold text-foreground">{formatPrice(order)}</div>
						<div className={`text-sm font-bold tracking-wide ${paymentClass}`}>{paymentLabel}</div>
					</div>
				</DialogDescription>

				<div className="my-2 border-t border-border/60" />

				<div className="space-y-1">
					{order.customerName ? <div className="text-base font-bold text-foreground">{order.customerName}</div> : null}
					{order.customerEmail ? <div className="text-sm text-muted-foreground">{order.customerEmail}</div> : null}
					{order.customerPhone ? <div className="text-sm text-muted-foreground">{order.customerPhone}</div> : null}
				</div>

				{order.notes ? (
					<div className="mt-3 rounded-lg bg-muted p-3">
						<div className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Customer note</div>
						<div className="text-sm text-foreground">{order.notes}</div>
					</div>
				) : null}

				<div className="flex justify-end gap-2 pt-3">
					<button
						type="button"
						onClick={() => onOpenChange(false)}
						className="rounded-full bg-muted px-4 py-2 text-sm font-semibold text-foreground transition hover:bg-secondary"
					>
						Close
					</button>
					{canCancel ? (
						<button
							type="button"
							disabled={!canManage}
							onClick={() => onCancel?.(order)}
							className="rounded-full bg-muted px-4 py-2 text-sm font-semibold text-foreground transition hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-50"
							title={canManage ? undefined : "You don't have permission to manage orders"}
						>
							Cancel order
						</button>
					) : null}
					{canConfirm ? (
						<button
							type="button"
							data-testid="detail-confirm-order"
							disabled={!canManage}
							onClick={() => onConfirm?.(order)}
							className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
							title={canManage ? undefined : "You don't have permission to manage orders"}
						>
							Confirm order
						</button>
					) : null}
					{canMarkComplete ? (
						<button
							type="button"
							disabled={!canManage}
							onClick={() => onComplete?.(order)}
							className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
							title={canManage ? undefined : "You don't have permission to manage orders"}
						>
							Mark complete
						</button>
					) : null}
				</div>
			</DialogContent>
		</Dialog>
	);
}
